import { useState, useEffect } from 'react';
import { Check, Download, Copy, ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { CelebrationAnimation } from '../components/CelebrationAnimation';
import { CountdownRedirect } from '../components/CountdownRedirect';
import { useSound } from '../contexts/SoundContext';

interface LicenseData {
  licenseKey: string;
  license: {
    id: string;
    tier: string;
    expiresAt: string;
    maxOptimizations: number;
  };
}

export function PaymentSuccess() {
  const navigate = useNavigate();
  const { playSound } = useSound();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [licenseData, setLicenseData] = useState<LicenseData | null>(null);
  const [copied, setCopied] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);

  useEffect(() => {
    const generateLicense = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');

      if (!sessionId) {
        setError('No session ID found');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-license`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ sessionId }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to generate license');
        }

        const data = await response.json();
        setLicenseData(data);
        setShowCelebration(true);
        playSound('celebration');
      } catch (err) {
        console.error('License generation error:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    generateLicense();
  }, [playSound]);

  const copyLicenseKey = () => {
    if (licenseData?.licenseKey) {
      navigator.clipboard.writeText(licenseData.licenseKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownloadPlugin = () => {
    window.location.href = 'https://ptfvgyrfluuxgnjnlsps.supabase.co/storage/v1/object/public/plugin-releases/resume-doctor-ai-v6.3.0.zip';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing Your License...</h2>
          <p className="text-gray-600">This will only take a moment</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl p-8 shadow-xl border-2 border-red-200">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-4xl">⚠️</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Something Went Wrong</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-blue-700 transition-colors"
            >
              Return Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50 py-12 px-4">
      {showCelebration && <CelebrationAnimation />}
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
            <CheckCircle2 className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Payment Successful!
          </h1>
          <p className="text-xl text-gray-600">
            Your license has been generated and is ready to use
          </p>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-xl border-2 border-green-200 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Your License Key</h2>

          <div className="bg-gradient-to-r from-blue-50 to-emerald-50 rounded-xl p-6 mb-6 border-2 border-blue-200">
            <div className="flex items-center justify-between gap-4">
              <code className="text-2xl font-bold text-blue-600 font-mono flex-1 break-all">
                {licenseData?.licenseKey}
              </code>
              <button
                onClick={copyLicenseKey}
                className="flex-shrink-0 bg-blue-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                {copied ? (
                  <>
                    <Check className="w-5 h-5" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-5 h-5" />
                    Copy
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Plan</div>
              <div className="text-lg font-bold text-gray-900 capitalize">
                {licenseData?.license.tier}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Monthly Limit</div>
              <div className="text-lg font-bold text-gray-900">
                {licenseData?.license.maxOptimizations === 999999
                  ? 'Unlimited'
                  : licenseData?.license.maxOptimizations}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Expires</div>
              <div className="text-lg font-bold text-gray-900">
                {licenseData?.license.expiresAt
                  ? new Date(licenseData.license.expiresAt).toLocaleDateString()
                  : 'Never'}
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-yellow-800 font-semibold">
              ⚠️ Important: Save this license key securely. You'll need it to activate the plugin.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-xl border-2 border-blue-200 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Next Steps</h2>

          <div className="space-y-6">
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                  1
                </div>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Download the Plugin</h3>
                <p className="text-gray-600 mb-3">
                  Download the WordPress plugin ZIP file to your computer
                </p>
                <button
                  onClick={handleDownloadPlugin}
                  className="bg-gradient-to-r from-blue-600 to-emerald-600 text-white px-6 py-3 rounded-lg font-bold hover:shadow-lg transition-all duration-300 flex items-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  Download Plugin v6.3.0
                </button>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                  2
                </div>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Install on WordPress</h3>
                <p className="text-gray-600">
                  Go to your WordPress Admin → Plugins → Add New → Upload Plugin, then select the ZIP
                  file and click Install
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                  3
                </div>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Configure Supabase Settings</h3>
                <p className="text-gray-600">
                  Activate the plugin, then go to Resume Doctor → Settings in your WordPress admin to
                  enter your Supabase credentials
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 font-bold">
                  4
                </div>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Start Earning!</h3>
                <p className="text-gray-600">
                  Add the [resume_doctor] shortcode to any page and start offering resume optimization
                  services to your visitors
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-emerald-600 to-blue-600 rounded-2xl p-8 text-white text-center mb-8">
          <h3 className="text-2xl font-bold mb-4">Need Help Getting Started?</h3>
          <p className="text-emerald-100 mb-6">
            Check out our comprehensive documentation and quick start guide
          </p>
          <button
            onClick={() => navigate('/wordpress-plugin#documentation')}
            className="bg-white text-blue-600 px-6 py-3 rounded-lg font-bold hover:bg-emerald-50 transition-all duration-300 inline-flex items-center justify-center gap-2"
          >
            View Documentation
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-xl border-2 border-blue-200">
          <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Ready to Get Started?</h3>
          <p className="text-gray-600 mb-6 text-center">
            Head to your dashboard to manage your license and download the plugin anytime
          </p>
          <CountdownRedirect
            seconds={5}
            redirectPath="/my-sites"
            skipButtonText="Skip and Go to Dashboard Now"
          />
        </div>
      </div>
    </div>
  );
}
